<template>
  <RevAccordionItem>
    <template #prefix><IconLocationPinOutlined /></template>
    <template #title>{{ i18n(translations.title) }}</template>
    <template #description>
      <span data-test="description">
        <span v-if="error">
          {{ i18n(translations.errorDescription) }}
        </span>
        <span v-else-if="!mostRecentProductReturn">
          {{ i18n(translations.noProductReturnDescription) }}
        </span>
        <span v-else data-allow-mismatch="text">
          <template
            v-if="
              !mostRecentProductReturn.datePickedUp &&
              !mostRecentProductReturn.dateDelivered &&
              mostRecentProductReturn.creationDate
            "
          >
            {{
              i18n(translations.creationDate, {
                date: new Date(mostRecentProductReturn.creationDate),
              })
            }}
          </template>

          <PickedUpDate
            v-if="
              mostRecentProductReturn.datePickedUp &&
              !mostRecentProductReturn.dateDelivered
            "
            :date="mostRecentProductReturn.datePickedUp"
            :is-handled-by-backrepair="
              mostRecentProductReturn.isHandledByBackrepair
            "
            :is-one-way="isOneWay(mostRecentProductReturn)"
          />

          <DeliveredDate
            v-if="mostRecentProductReturn.dateDelivered"
            :date="mostRecentProductReturn.dateDelivered"
            :is-handled-by-backrepair="
              mostRecentProductReturn.isHandledByBackrepair
            "
            :is-one-way="isOneWay(mostRecentProductReturn)"
          />
        </span>
      </span>
    </template>
    <template #body>
      <RevInfoBlock
        v-if="error"
        :content="i18n(translations.errorStateContent)"
        :icon="IconWarning"
        :title="i18n(translations.errorStateTitle)"
        variant="danger"
      />
      <p v-if="isEmpty(productReturns) && !error">
        {{ i18n(translations.noProductReturnText) }}
      </p>
      <div v-if="!error" class="space-y-16">
        <ul class="space-y-16" data-test="product-returns">
          <li
            v-for="productReturn in productReturns"
            :key="productReturn.productReturnId"
          >
            <ProductReturn :product-return />
          </li>
        </ul>

        <div v-if="lastOneWayProductReturn && lastOneWayProductReturn.shipment">
          <p class="body-2-bold">
            {{
              i18n(translations.customerAddressTitle, {
                date: new Date(lastOneWayProductReturn.creationDate),
              })
            }}
          </p>
          <Address
            :address="lastOneWayProductReturn.shipment.dispatchAddress"
          />
        </div>
      </div>
    </template>
  </RevAccordionItem>
</template>

<script setup lang="ts">
import { useAsyncData } from '#imports'
import { computed } from 'vue'

import type { DateTime } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconLocationPinOutlined } from '@ds/icons/IconLocationPinOutlined'
import { IconWarning } from '@ds/icons/IconWarning'

import type { CareFolder } from '~/api/care-folders/types/care-folder'
import type { ProductReturn as GetCareFolderProductReturns } from '~/api/care-folders/types/product-return'

import Address from '../Address.vue'

import DeliveredDate from './ProductReturn/DeliveredDate.vue'
import PickedUpDate from './ProductReturn/PickedUpDate.vue'
import { isOneWay } from './ProductReturn/ProductReturn.utils'
import ProductReturn from './ProductReturn/ProductReturn.vue'
import translations from './Returns.translations'

const i18n = useI18n()

const props = defineProps<{
  customerRequestId: CareFolder['customerRequestId']
}>()

const $httpFetch = useHttpFetch()
const { data: productReturns, error } = useAsyncData(() =>
  $httpFetch('/bm/customer_request/:customerRequestId/product_returns', {
    pathParams: {
      customerRequestId: props.customerRequestId,
    },
  }),
)

const lastOneWayProductReturn = computed(() => {
  const oneWayProductReturns = productReturns?.value?.filter(
    (productReturn) => {
      return (
        productReturn.kind === 'CUSTOMER_TO_MERCHANT' ||
        productReturn.kind === 'CUSTOMER_TO_FACTORY'
      )
    },
  )

  if (!oneWayProductReturns) {
    return null
  }

  return oneWayProductReturns[oneWayProductReturns.length - 1]
})

function getMostRecentDate(productReturn: GetCareFolderProductReturns) {
  const filteredDates = [
    productReturn.creationDate,
    productReturn.dateDelivered,
    productReturn.datePickedUp,
  ].filter((date) => date) as DateTime[]
  const dates = filteredDates.map((date) => new Date(date).getTime())

  return new Date(Math.max(...dates))
}

const mostRecentProductReturn = computed(() => {
  if (!productReturns.value) return null

  return productReturns.value.reduce<GetCareFolderProductReturns | null>(
    (mostRecent, productReturn) => {
      if (!mostRecent) return productReturn
      if (getMostRecentDate(productReturn) > getMostRecentDate(mostRecent)) {
        return productReturn
      }

      return mostRecent
    },
    null,
  )
})
</script>
